import { mapDTO } from '@twistezo/ts-dto-mapper'
import { camelCase } from 'change-case'
import _ from 'lodash'
import { PartialDeep } from 'type-fest'

import {
  BiddersInput,
  PrebidModuleInput,
  VariantsSchema,
} from 'features/ab-tests/forms/AbTestsCommonForm/abTestsCommonSchema'
import { DomainBidderPriority } from 'features/globalSetup/types/bidderPriority'
import { UnfilledRecoveryPrebidBehaviour } from 'features/inventory/types/unfilledRecoveryPrebidBehaviour'
import { floatPercentFromDTO } from 'utils/number'
import { withDefined } from '../../useCreateAbTest/api/mapper'
import {
  AbTestVariantAttributesDTO,
  PrebidBidderInputDTO,
  PrebidModuleInputDTO,
} from '../../useCreateAbTest/api/types'

type From = PartialDeep<AbTestVariantAttributesDTO>
type To = VariantsSchema

export const mapVariant = (from: From): To =>
  mapDTO<From, To>({ from }).transform(
    (schemaDTO: From): To =>
      ({
        ...schemaDTO,

        ...withDefined('general', schemaDTO.general, general => ({
          ...general,
        })),

        ...withDefined('prebidStack', schemaDTO.prebidStack, prebidStack => ({
          ...prebidStack,
          ...withDefined('bidders', prebidStack.bidders, regroupBiddersItems),
          ...withDefined('prebidModules', prebidStack.prebidModules, modules => ({
            ...withDefined('analytics', modules.analytics, regroupPrebidModuleItems),
            ...withDefined('general', modules.general, regroupPrebidModuleItems),
            ...withDefined('userId', modules.userId, regroupPrebidModuleItems),
            ...withDefined('vendorSpecific', modules.vendorSpecific, regroupPrebidModuleItems),
          })),
        })),

        ...withDefined('refresher', schemaDTO.refresher, refresher => ({
          ...refresher,
          ...withDefined('basicSettings', refresher.basicSettings, basicSettings => ({
            ...basicSettings,
            ...withDefined(
              'desktopAdThreshold',
              basicSettings.desktopAdThreshold,
              floatPercentFromDTO,
            ),
            ...withDefined(
              'mobileAdThreshold',
              basicSettings.mobileAdThreshold,
              floatPercentFromDTO,
            ),
          })),
        })),

        ...withDefined('trafficShare', schemaDTO.trafficShare, floatPercentFromDTO),

        ...withDefined('unfilledRecovery', schemaDTO.unfilledRecovery, unfilledRecovery => ({
          ...unfilledRecovery,
          ...withDefined(
            'prebidBehaviourType',
            unfilledRecovery.prebidBehaviourType,
            prebidBehaviourType =>
              camelCase(prebidBehaviourType) as UnfilledRecoveryPrebidBehaviour,
          ),
        })),

        ...withDefined('viewabilityTools', schemaDTO.viewabilityTools, viewabilityTools => ({
          ...viewabilityTools,
          ...withDefined('lazyLoading', viewabilityTools.lazyLoading, lazyLoading => ({
            ...lazyLoading,
            ...withDefined('gamAdRenderMargin', lazyLoading.gamAdRenderMargin, floatPercentFromDTO),
            ...withDefined(
              'gamAdRequestMargin',
              lazyLoading.gamAdRequestMargin,
              floatPercentFromDTO,
            ),
            ...withDefined(
              'kvTargetingAndPrebidAuction',
              lazyLoading.kvTargetingAndPrebidAuction,
              floatPercentFromDTO,
            ),
            ...withDefined('lazyLoadType', lazyLoading.lazyLoadType, camelCase),
          })),
        })),
      }) as To,
  )

export const regroupPrebidModuleItems = (items: PrebidModuleInputDTO[]): PrebidModuleInput =>
  _.chain(items)
    .keyBy('domainPrebidModuleId')
    .mapValues(({ enabled }) => ({ enabled }))
    .value()

export const regroupBiddersItems = (items: PrebidBidderInputDTO[]): BiddersInput =>
  _.chain(items)
    .keyBy('domainBidderId')
    .mapValues(({ csEnabled, priority, s2sEnabled }) => ({
      csEnabled,
      priority: camelCase(priority) as DomainBidderPriority,
      s2sEnabled,
    }))
    .value()
